import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import {
  ArrowRight,
  Check,
  ChevronDown,
  ChevronUp,
  GripVertical,
  Settings2,
  Settings,
  Minus,
  Sunrise,
  Sunset,
  Timer,
  Sailboat,
  Sun,
  X,
} from 'lucide-react';
import { usePlatform } from '../store';
import { PRIVACY_EVENT, privacyHidden } from '../privacy';
import {
  formatWorkdayDuration,
  getWorkdayState,
  parseWorkdaySettings,
  validWorkTime,
  workdayStorageKey,
  WORKDAYS,
  DEFAULT_WORKDAY,
  type WorkdayPosition,
  type WorkdaySettings,
} from '../workday';
import { Modal } from './Modal';
import './workday-countdown.css';
import { IslandBadge } from './workday/IslandBadge';
import { IslandCardSurface } from './workday/IslandCardSurface';

type WorkdayContext = {
  settings: WorkdaySettings;
  update: (patch: Partial<WorkdaySettings>) => void;
  configure: () => void;
};
const Context = createContext<WorkdayContext>(null!);
function readSettings(key: string) {
  try {
    const saved = localStorage.getItem(key);
    if (key.endsWith(':mobile') && !saved) {
      return { ...parseWorkdaySettings(localStorage.getItem(key.slice(0, -7))), enabled: false };
    }
    return parseWorkdaySettings(saved);
  } catch {
    return { ...parseWorkdaySettings(null), enabled: !key.endsWith(':mobile') };
  }
}

// Touch phones remain in the mobile settings bucket when rotated to a wide viewport.
const MOBILE_WORKDAY_QUERY = '(max-width: 760px), (hover: none) and (pointer: coarse)';

export function WorkdayProvider({ children }: { children: ReactNode }) {
  const { data, notify } = usePlatform();
  const { pathname } = useLocation();
  const [mobile, setMobile] = useState(() => window.matchMedia(MOBILE_WORKDAY_QUERY).matches);
  useEffect(() => {
    const query = window.matchMedia(MOBILE_WORKDAY_QUERY);
    const sync = () => setMobile(query.matches);
    query.addEventListener('change', sync);
    return () => query.removeEventListener('change', sync);
  }, []);
  const storageKey = workdayStorageKey(data?.user?.id) + (mobile ? ':mobile' : '');
  const loaded = useMemo(() => readSettings(storageKey), [storageKey]);
  const [record, setRecord] = useState({ key: storageKey, settings: loaded });
  const settings = record.key === storageKey ? record.settings : loaded;
  const latest = useRef(settings);
  latest.current = settings;
  const [configuring, setConfiguring] = useState(false);
  const [hidden, setHidden] = useState(privacyHidden);
  const [portal, setPortal] = useState<Element>(() => document.fullscreenElement || document.body);
  const player = pathname.startsWith('/play/');
  const publicPage = !/^\/(admin(?:\/|$|\.html)|login(?:\/|$|\.html))/.test(pathname);
  const update = useCallback(
    (patch: Partial<WorkdaySettings>) => {
      const next = { ...latest.current, ...patch };
      latest.current = next;
      setRecord({ key: storageKey, settings: next });
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        notify('设置已应用，但当前浏览器无法保存，关闭页面后需重新设置');
      }
    },
    [storageKey, notify],
  );
  useEffect(() => {
    setConfiguring(false);
    const sync = (event: StorageEvent) => {
      if (event.storageArea === localStorage && (event.key === storageKey || event.key === null))
        setRecord({ key: storageKey, settings: readSettings(storageKey) });
    };
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, [storageKey]);
  useEffect(() => {
    const privacy = () => {
      const value = privacyHidden();
      setHidden(value);
      if (value) setConfiguring(false);
    };
    const fullscreen = () => setPortal(document.fullscreenElement || document.body);
    window.addEventListener(PRIVACY_EVENT, privacy);
    document.addEventListener('fullscreenchange', fullscreen);
    return () => {
      window.removeEventListener(PRIVACY_EVENT, privacy);
      document.removeEventListener('fullscreenchange', fullscreen);
    };
  }, []);
  useEffect(() => setConfiguring(false), [pathname]);
  return (
    <Context.Provider value={{ settings, update, configure: () => setConfiguring(true) }}>
      {children}
      {data &&
        publicPage &&
        !hidden &&
        settings.enabled &&
        createPortal(<FloatingCountdown key={storageKey} player={player} />, portal)}
      {data && publicPage && !hidden && configuring && (
        <WorkdaySettingsDialog onClose={() => setConfiguring(false)} />
      )}
    </Context.Provider>
  );
}

export function WorkdayButton({ label = false, onOpen }: { label?: boolean; onOpen?: () => void }) {
  const { configure } = useContext(Context);
  return (
    <button
      className="icon-button workday-entry"
      aria-label="上下班倒计时设置"
      aria-haspopup="dialog"
      title="上下班倒计时"
      onClick={() => {
        onOpen?.();
        configure();
      }}
    >
      <Timer size={18} />
      {label && <span>上下班倒计时</span>}
    </button>
  );
}

function FloatingCountdown({ player, demoProgress }: { player: boolean; demoProgress?: number }) {
  const { settings, update, configure } = useContext(Context);
  const { notify, data } = usePlatform();
  const [now, setNow] = useState(() => new Date());
  const [metrics, setMetrics] = useState({
    width: 56,
    height: 56,
    vw: window.innerWidth,
    vh: window.innerHeight,
    top: player ? 74 : 82,
  });
  const [moving, setMoving] = useState<WorkdayPosition | null>(null);
  const element = useRef<HTMLElement>(null);
  const suppressClick = useRef(false);
  const drag = useRef<{
    id: number;
    x: number;
    y: number;
    left: number;
    top: number;
    point: WorkdayPosition | null;
  } | null>(null);
  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | undefined;
    const restart = () => {
      clearInterval(timer);
      setNow(new Date());
      if (!document.hidden) timer = setInterval(() => setNow(new Date()), 1000);
    };
    restart();
    document.addEventListener('visibilitychange', restart);
    window.addEventListener('focus', restart);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', restart);
      window.removeEventListener('focus', restart);
    };
  }, []);
  useLayoutEffect(() => {
    const measure = () => {
      if (!element.current) return;
      const rect = element.current.getBoundingClientRect();
      const toolbar = document.querySelector(player ? '.player-toolbar' : '.topbar');
      setMetrics({
        width: rect.width,
        height: rect.height,
        vw: window.innerWidth,
        vh: window.innerHeight,
        top: Math.max(12, (toolbar?.getBoundingClientRect().bottom || 70) + 12),
      });
    };
    const observer = new ResizeObserver(measure);
    if (element.current) observer.observe(element.current);
    const toolbar = document.querySelector(player ? '.player-toolbar' : '.topbar');
    if (toolbar) observer.observe(toolbar);
    window.addEventListener('resize', measure);
    window.visualViewport?.addEventListener('resize', measure);
    measure();
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
      window.visualViewport?.removeEventListener('resize', measure);
    };
  }, [player]);
  const gutter = metrics.vw <= 760 ? 12 : 24;
  const minTop = metrics.top;
  const bottom = (metrics.vw <= 760 && !player ? 88 : gutter) + (settings.collapsed ? 0 : 52);
  const spanX = Math.max(0, metrics.vw - metrics.width - 2 * gutter);
  const spanY = Math.max(0, metrics.vh - metrics.height - minTop - bottom);
  const position = moving || settings.position;
  const style: CSSProperties = position
    ? {
        left: gutter + position.x * spanX,
        top: minTop + position.y * spanY,
        right: 'auto',
        bottom: 'auto',
      }
    : { right: gutter, bottom };
  const normalize = (left: number, top: number): WorkdayPosition => ({
    x: spanX ? Math.max(0, Math.min(1, (left - gutter) / spanX)) : 0,
    y: spanY ? Math.max(0, Math.min(1, (top - minTop) / spanY)) : 0,
  });
  const beginDrag = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0 || !element.current) return;
    const rect = element.current.getBoundingClientRect();
    suppressClick.current = false;
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      left: rect.left,
      top: rect.top,
      point: null,
    };
  };
  const moveDrag = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const start = drag.current;
    if (!start || start.id !== event.pointerId) return;
    if (Math.hypot(event.clientX - start.x, event.clientY - start.y) < 5 && !start.point) return;
    suppressClick.current = true;
    const point = normalize(
      start.left + event.clientX - start.x,
      start.top + event.clientY - start.y,
    );
    start.point = point;
    setMoving(point);
  };
  const endDrag = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (drag.current?.id !== event.pointerId) return;
    if (event.type === 'pointerup' && drag.current.point) update({ position: drag.current.point });
    drag.current = null;
    setMoving(null);
  };
  const liveState = getWorkdayState(settings, now);
  const state =
    demoProgress === undefined
      ? liveState
      : {
          ...liveState,
          phase: 'working' as const,
          progress: demoProgress,
          remaining: Math.round((1 - demoProgress) * 8 * 3600),
          lunch: false,
        };
  useEffect(() => {
    if (
      demoProgress !== undefined ||
      !settings.reminderEnabled ||
      state.phase !== 'working' ||
      state.remaining > settings.reminderMinutes * 60
    )
      return;
    const key = workdayStorageKey(data?.user?.id) + ':reminded:' + state.shift.end.toISOString();
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    } catch {
      return;
    }
    notify(`还有 ${Math.ceil(state.remaining / 60)} 分钟下班，准备收工啦`);
  }, [state.remaining, state.phase, settings.reminderEnabled, settings.reminderMinutes, notify]);
  const counting = state.phase === 'working';
  const clock = formatWorkdayDuration(state.remaining);
  const phaseLabel = {
    working: '距离下班',
    before: '睡个好觉',
    finished: '今天已下班',
    rest: state.holiday ? `${state.holiday} · 休假中` : '休假中',
  }[state.phase];
  const progress = Math.round(state.progress * 100);
  const night = settings.end < settings.start;
  const grip = (
    <button
      className="workday-grip"
      aria-label="移动倒计时"
      title="拖动调整位置，也可用方向键移动"
      onPointerDown={beginDrag}
      onPointerMove={moveDrag}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onLostPointerCapture={endDrag}
      onKeyDown={(event) => {
        const directions: Record<string, [number, number]> = {
          ArrowLeft: [-16, 0],
          ArrowRight: [16, 0],
          ArrowUp: [0, -16],
          ArrowDown: [0, 16],
        };
        const direction = directions[event.key];
        if (!direction || !element.current) return;
        event.preventDefault();
        event.stopPropagation();
        const rect = element.current.getBoundingClientRect();
        update({ position: normalize(rect.left + direction[0], rect.top + direction[1]) });
      }}
    >
      <GripVertical size={15} />
    </button>
  );
  return (
    <aside
      ref={element}
      className={`workday-floating ${settings.collapsed ? 'is-compact' : ''} ${moving ? 'is-dragging' : ''}`}
      style={style}
      aria-label={state.phase === 'rest' ? '海岛休假' : '下班倒计时'}
      data-phase={state.phase}
    >
      {settings.collapsed ? (
        <button
          className="workday-island-trigger"
          aria-label={state.phase === 'rest' ? '展开休假面板' : '展开下班倒计时'}
          onPointerDown={beginDrag}
          onPointerMove={moveDrag}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onLostPointerCapture={endDrag}
          onClick={(event) => {
            if (event.detail === 0 || !suppressClick.current) update({ collapsed: false });
          }}
        >
          <IslandBadge progress={state.progress} resting={state.phase === 'rest'} />
          <span className="workday-island-countdown" aria-hidden="true">
            {state.phase === 'rest' ? '度假中' : counting ? clock : phaseLabel}
          </span>
          <span className="workday-island-tooltip">
            {counting ? `${phaseLabel} ${clock}` : phaseLabel}
          </span>
        </button>
      ) : (
        <>
          <IslandCardSurface height={metrics.height} />
          {state.phase === 'rest' ? (
            <img
              className="workday-holiday-surface"
              src="/art/workday-v2/holiday-panel-flat-top.webp"
              alt=""
              aria-hidden="true"
              draggable={false}
            />
          ) : (
            <>
              <div className="workday-card-art" aria-hidden="true" />
            </>
          )}
          <div className="workday-heading">
            {grip}
            <span className="workday-reference-brand" aria-hidden="true" />
            <span className="workday-heading-title">摸鱼岛</span>
            <button
              className="workday-action"
              aria-label={state.phase === 'rest' ? '作息设置' : '设置上下班时间'}
              onClick={configure}
            >
              {state.phase === 'rest' ? <Settings size={19} /> : <Settings2 size={18} />}
            </button>
            <button
              className="workday-action"
              aria-label={state.phase === 'rest' ? '收起休假面板' : '收起倒计时'}
              onClick={() => update({ collapsed: true })}
            >
              <Minus size={20} />
            </button>
          </div>
          <div className="workday-readout">
            <div className="workday-phase">
              {state.phase === 'rest'
                ? state.holiday
                  ? `${state.holiday.replace(/节$/, '')}假期`
                  : '休假时光'
                : phaseLabel}
              {state.lunch && <small className="workday-lunch-badge">午休中</small>}
            </div>
            {counting ? (
              <div className="workday-digits" role="timer" aria-live="off">
                {clock}
              </div>
            ) : (
              <div className="workday-rest">
                {state.phase === 'rest' ? (
                  <>
                    悠闲休假中<span>今天，慢慢来就好。</span>
                  </>
                ) : (
                  <>
                    {state.phase === 'before' ? '睡个好觉' : '好好休息'}
                    <span>放下忙碌，安心休息。</span>
                  </>
                )}
              </div>
            )}
            {state.phase !== 'rest' && state.phase !== 'before' && (
              <>
                <div className="workday-progress-row">
                  <span>今日进度</span>
                  <strong>{progress}%</strong>
                </div>
                <div
                  className="workday-sea-track"
                  role="progressbar"
                  aria-label="工作时段完成进度"
                  aria-valuenow={progress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <svg viewBox="0 0 300 48" preserveAspectRatio="none" aria-hidden="true">
                    <path
                      d="M6 27 Q55 3 110 25 T205 27 T294 27"
                      fill="none"
                      stroke="#c7e9ff"
                      strokeWidth="5"
                      strokeLinecap="round"
                    />
                    <path
                      d="M6 27 Q55 3 110 25 T205 27 T294 27"
                      pathLength="100"
                      fill="none"
                      stroke="#328bff"
                      strokeWidth="5"
                      strokeLinecap="round"
                      strokeDasharray={`${progress} 100`}
                    />
                  </svg>
                  <svg
                    className="workday-boat"
                    viewBox="0 0 40 42"
                    width="36"
                    height="38"
                    aria-hidden="true"
                    style={{
                      left: `${3 + state.progress * 90}%`,
                      top: `${11 - Math.sin(state.progress * Math.PI * 3) * 6}px`,
                    }}
                  >
                    <path d="M21 2 L21 30 L6 30 Z" fill="#2786ed" stroke="#fff" strokeWidth="1" />
                    <path d="M24 9 L35 29 L24 29 Z" fill="#b2e5ff" stroke="#fff" strokeWidth="1" />
                    <path
                      d="M3 33 Q20 37 38 31 L31 40 L12 40 Z"
                      fill="#2269b8"
                      stroke="#fff"
                      strokeWidth="1"
                    />
                    <path d="M22 2 V34" stroke="#154c8a" strokeWidth="1.2" />
                  </svg>
                </div>
                <button
                  className="workday-schedule"
                  onClick={configure}
                  aria-label="修改上下班时间"
                >
                  <Sunrise size={18} />
                  <span>{settings.start} 上班</span>
                  <Sunset size={18} />
                  <span>
                    {night ? '次日 ' : ''}
                    {settings.end} 下班
                  </span>
                </button>
              </>
            )}
            <div className="workday-finish-note is-unified">
              <span>
                {state.phase === 'rest'
                  ? '今日宜放松'
                  : state.phase === 'before'
                    ? '今晚，好梦'
                    : `${settings.end}，准时收工`}
              </span>
            </div>
          </div>
          <img
            className="workday-shared-footer"
            src="/art/workday-v2/shared-footer.webp"
            alt=""
            aria-hidden="true"
            draggable={false}
          />
          <button
            className="workday-anchor"
            aria-label={state.phase === 'rest' ? '收起休假面板' : '收起倒计时'}
            onClick={() => update({ collapsed: true })}
          >
            <IslandBadge progress={state.progress} resting={state.phase === 'rest'} />
          </button>
        </>
      )}
    </aside>
  );
}

function WorkdaySettingsDialog({ onClose }: { onClose: () => void }) {
  const { settings, update } = useContext(Context);
  const [draft, setDraft] = useState(settings);
  const [error, setError] = useState('');
  const night = draft.end < draft.start;
  return (
    <Modal title="作息设置" onClose={onClose} className="workday-dialog">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          const fields = new FormData(event.currentTarget);
          const start = fields.get('start');
          const end = fields.get('end');
          if (!validWorkTime(start) || !validWorkTime(end))
            return setError('请填写完整的上下班时间');
          if (start === end) return setError('上下班时间不能相同');
          if (
            draft.lunchEnabled &&
            (!validWorkTime(draft.lunchStart) ||
              !validWorkTime(draft.lunchEnd) ||
              draft.lunchStart >= draft.lunchEnd)
          )
            return setError('请填写有效的午休时段');
          if (draft.lunchEnabled) {
            const minutes = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3));
            const a = minutes(start),
              b = minutes(end) + (end < start ? 1440 : 0);
            const l =
              minutes(draft.lunchStart) + (end < start && draft.lunchStart < start ? 1440 : 0);
            const r = l + minutes(draft.lunchEnd) - minutes(draft.lunchStart);
            if (l < a || r > b || r - l >= b - a)
              return setError('午休需要在工作时段内，且短于整个班次');
          }
          if (!draft.days.length) return setError('请至少选择一个工作日');
          update({ ...draft, start, end });
          onClose();
        }}
      >
        <div className="workday-settings-body">
          <div className="workday-time-fields">
            <label>
              <span>
                <Sunrise size={17} />
                上班时间
              </span>
              <input
                type="time"
                name="start"
                aria-label="上班时间"
                value={draft.start}
                required
                onInput={(event) => {
                  setDraft({ ...draft, start: event.currentTarget.value });
                  setError('');
                }}
                onChange={(event) => {
                  setDraft({ ...draft, start: event.target.value });
                  setError('');
                }}
              />
            </label>
            <span className="workday-time-arrow">
              <ArrowRight size={19} />
            </span>
            <label>
              <span>
                <Sunset size={17} />
                下班时间
              </span>
              <input
                type="time"
                name="end"
                aria-label="下班时间"
                value={draft.end}
                required
                onInput={(event) => {
                  setDraft({ ...draft, end: event.currentTarget.value });
                  setError('');
                }}
                onChange={(event) => {
                  setDraft({ ...draft, end: event.target.value });
                  setError('');
                }}
              />
            </label>
          </div>
          {night && <p className="workday-night-note">跨天班次 · 次日 {draft.end} 下班</p>}
          <div className="workday-extra-section">
            <label className="workday-display-toggle">
              <strong>午休</strong>
              <input
                type="checkbox"
                role="switch"
                aria-label="午休"
                checked={draft.lunchEnabled}
                onChange={(e) => setDraft({ ...draft, lunchEnabled: e.target.checked })}
              />
              <span className="workday-switch" />
            </label>
            {draft.lunchEnabled && (
              <div className="workday-lunch-fields">
                <input
                  type="time"
                  aria-label="午休开始"
                  value={draft.lunchStart}
                  onChange={(e) => setDraft({ ...draft, lunchStart: e.target.value })}
                />
                <span>—</span>
                <input
                  type="time"
                  aria-label="午休结束"
                  value={draft.lunchEnd}
                  onChange={(e) => setDraft({ ...draft, lunchEnd: e.target.value })}
                />
              </div>
            )}
          </div>
          <label className="workday-calendar-toggle">
            <input
              type="checkbox"
              checked={draft.chinaHolidays}
              onChange={(e) => setDraft({ ...draft, chinaHolidays: e.target.checked })}
            />
            跟随中国法定节假日与调休
            <small>已收录 2026 年官方安排，其他年份按所选工作日。</small>
          </label>
          <fieldset className="workday-days">
            <legend>工作日</legend>
            <div>
              {WORKDAYS.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  aria-label={`星期${label}`}
                  aria-pressed={draft.days.includes(value)}
                  onClick={() => {
                    setDraft({
                      ...draft,
                      days: draft.days.includes(value)
                        ? draft.days.filter((day) => day !== value)
                        : [...draft.days, value],
                    });
                    setError('');
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </fieldset>
          <div className="workday-extra-section">
            <label className="workday-display-toggle">
              <strong>下班提醒</strong>
              <input
                type="checkbox"
                role="switch"
                aria-label="下班提醒"
                checked={draft.reminderEnabled}
                onChange={(e) => setDraft({ ...draft, reminderEnabled: e.target.checked })}
              />
              <span className="workday-switch" />
            </label>
            {draft.reminderEnabled && (
              <select
                aria-label="提前提醒时间"
                value={draft.reminderMinutes}
                onChange={(e) => setDraft({ ...draft, reminderMinutes: Number(e.target.value) })}
              >
                <option value={5}>提前 5 分钟</option>
                <option value={10}>提前 10 分钟</option>
                <option value={15}>提前 15 分钟</option>
              </select>
            )}
          </div>
          <label className="workday-display-toggle">
            <span>
              <strong>站内悬浮</strong>
              <small>浏览页面、玩游戏时都能看到</small>
            </span>
            <input
              type="checkbox"
              role="switch"
              aria-label="悬浮显示"
              checked={draft.enabled}
              onChange={(event) => setDraft({ ...draft, enabled: event.target.checked })}
            />
            <span className="workday-switch" aria-hidden="true" />
          </label>
          <p className="workday-local-note">按设备本地时间计算，设置保存在当前浏览器。</p>
          {error && (
            <p className="workday-error" role="alert">
              {error}
            </p>
          )}
        </div>
        <div className="workday-settings-footer">
          <button
            type="button"
            className="workday-reset"
            disabled={!draft.position}
            onClick={() => setDraft({ ...draft, position: null })}
          >
            恢复默认位置
          </button>
          <button type="submit" className="button primary">
            <Check size={16} />
            保存设置
          </button>
        </div>
      </form>
    </Modal>
  );
}

// Rendered only by the isolated local design-review entry, never routed in the public app.
export function WorkdayDesignPreview({ children }: { children: ReactNode }) {
  const [progress, setProgress] = useState<number | undefined>(0.62);
  const [settings, setSettings] = useState<WorkdaySettings>({
    ...DEFAULT_WORKDAY,
    collapsed: false,
    lunchEnabled: true,
    reminderEnabled: true,
  });
  const [configuring, setConfiguring] = useState(false);
  const [playing, setPlaying] = useState(false);
  useEffect(() => {
    if (!playing) return;
    const timer = window.setInterval(() => setProgress((p) => Math.min(1, (p ?? 0) + 0.002)), 40);
    return () => window.clearInterval(timer);
  }, [playing]);
  useEffect(() => {
    if (progress === 1) setPlaying(false);
  }, [progress]);
  return (
    <Context.Provider
      value={{
        settings,
        update: (patch) => setSettings((s) => ({ ...s, ...patch })),
        configure: () => setConfiguring(true),
      }}
    >
      {children}
      <div className="workday-design-controls">
        <strong>倒计时预览</strong>
        <input
          aria-label="工作日进度"
          type="range"
          min="0"
          max="1"
          step="0.001"
          value={progress ?? 0}
          onChange={(e) => {
            setPlaying(false);
            setProgress(Number(e.target.value));
          }}
        />
        <button
          onClick={() => {
            if (!playing && (progress === undefined || progress === 1)) setProgress(0);
            setPlaying(!playing);
          }}
        >
          {playing ? '暂停动画' : '播放一天'}
        </button>
        <button onClick={() => setProgress(0)}>刚上班</button>
        <button onClick={() => setProgress(0.58)}>下午</button>
        <button onClick={() => setProgress(0.98)}>临近下班</button>
        <button
          onClick={() => {
            setPlaying(false);
            setProgress(undefined);
          }}
        >
          真实时间 / 节假日
        </button>
        <button onClick={() => setSettings((s) => ({ ...s, collapsed: !s.collapsed }))}>
          展开 / 收起
        </button>
        <button onClick={() => setConfiguring(true)}>作息设置</button>
      </div>
      {settings.enabled && <FloatingCountdown player={false} demoProgress={progress} />}
      {configuring && <WorkdaySettingsDialog onClose={() => setConfiguring(false)} />}
    </Context.Provider>
  );
}
